// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../App';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, Star } from 'lucide-react';
import { LevelBadge } from '../components/UIComponents';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatBadge = ({ label, gold, 'data-testid': testId }) => (
  <span data-testid={testId} style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '6px', background: gold ? 'rgba(201,168,76,0.1)' : 'rgba(255,255,255,0.04)', color: gold ? '#C9A84C' : '#888780', border: `0.5px solid ${gold ? 'rgba(201,168,76,0.3)' : 'rgba(255,255,255,0.1)'}` }}>{label}</span>
);

// ── QUIZ ────────────────────────────────────────────────────────────────────
const QUESTIONS = [
  { q: 'Hai un fondo di emergenza?', opts: ['No, non ce l\'ho', 'Sì, meno di 3 mesi di spese', 'Sì, 3-6 mesi di spese', 'Sì, oltre 6 mesi di spese'] },
  { q: 'Quanto risparmi ogni mese?', opts: ['Nulla o quasi', 'Meno del 10% del reddito', '10-20% del reddito', 'Più del 20% del reddito'] },
  { q: 'Hai debiti ad alto interesse?', opts: ['Sì, carte revolving o prestiti >10%', 'Solo il mutuo prima casa', 'Mutuo + nessun altro debito', 'Nessun debito'] },
  { q: 'Investi regolarmente?', opts: ['No, non investo nulla', 'Qualcosa ma non regolare', 'Investo ogni mese con un piano', 'Ho un piano strutturato e diversificato'] },
  { q: 'Hai una polizza vita o invalidità?', opts: ['No, nessuna copertura', 'Solo quella del lavoro', 'Sì, una polizza base', 'Sì, copertura adeguata al mio reddito'] },
  { q: 'Stai pensando alla pensione?', opts: ['No, è troppo lontana', 'Ho il TFR in azienda ma nient\'altro', 'Ho un fondo pensione complementare', 'Ho un piano previdenziale strutturato'] },
  { q: 'Conosci la tua situazione fiscale?', opts: ['Non so quanto pago di tasse esatto', 'Conosco la mia aliquota IRPEF', 'Sfrutto alcune detrazioni fiscali', 'Ottimizzo attivamente la mia fiscalità'] },
];
const getResult = (score) => {
  if (score <= 5)  return { label: 'Salute finanziaria: Critica 🔴', color: '#E24B4A', desc: 'Hai basi molto fragili. Non sei solo — è il punto di partenza di molti italiani. SAPIENS è progettato esattamente per chi parte da zero.' };
  if (score <= 10) return { label: 'Da migliorare 🟡', color: '#BA7517', desc: 'Sei sulla strada giusta ma ci sono aree critiche da rafforzare. Con le basi giuste puoi migliorare significativamente in pochi mesi.' };
  if (score <= 16) return { label: 'Buona 🟢', color: '#1D9E75', desc: 'Hai basi solide. Ora puoi concentrarti sulla crescita del patrimonio e sull\'ottimizzazione fiscale.' };
  return { label: 'Eccellente ✨', color: '#C9A84C', desc: 'Sei tra il 10% degli italiani finanziariamente più consapevoli. SAPIENS ti aiuta a mantenere e migliorare questo livello.' };
};

const QuizSection = ({ navigate }) => {
  const [step, setStep] = useState(0); // -1 = not started, 0..6 = questions, 7 = results
  const [started, setStarted] = useState(false);
  const [answers, setAnswers] = useState([]);
  const score = answers.reduce((s, a) => s + a, 0);
  const result = getResult(score);

  const handleAnswer = (pts) => {
    const newAns = [...answers, pts];
    setAnswers(newAns);
    if (step + 1 >= QUESTIONS.length) {
      setStep(QUESTIONS.length);
      try { localStorage.setItem('sapiens_health_score', JSON.stringify({ score: newAns.reduce((s,a) => s+a,0), date: new Date().toISOString() })); } catch {}
    } else {
      setStep(step + 1);
    }
  };
  const reset = () => { setStep(0); setAnswers([]); setStarted(true); };
  const done = step >= QUESTIONS.length;

  return (
    <section id="quiz-section" className="py-12" style={{ background: '#181816' }} data-testid="quiz-section">
      <div className="container-wide max-w-2xl mx-auto px-4">
        <div className="text-center mb-7">
          <h2 className="font-bold text-[#E8E8E4]" style={{ fontSize: 26, marginBottom: 6 }}>Qual è la tua salute finanziaria?</h2>
          <p className="text-xs text-[#888780]">7 domande · 3 minuti · Nessuna registrazione richiesta</p>
        </div>
        {!started ? (
          <div className="text-center">
            <button onClick={() => setStarted(true)} className="font-semibold transition-all hover:opacity-90" style={{ background: '#C9A84C', color: '#3A2800', padding: '12px 32px', borderRadius: 8, fontSize: 15 }} data-testid="quiz-start-btn">
              Inizia il quiz →
            </button>
          </div>
        ) : done ? (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-center">
            <p className="text-4xl font-bold mb-2" style={{ color: result.color }}>{score}/21</p>
            <p className="text-lg font-semibold mb-3" style={{ color: result.color }} data-testid="quiz-result-label">{result.label}</p>
            <p className="text-sm text-[#888780] mb-6 max-w-lg mx-auto">{result.desc}</p>
            <div className="flex flex-wrap justify-center gap-3 mb-4">
              <button onClick={() => navigate('/percorsi')} className="font-medium text-sm px-5 py-2.5 rounded-lg transition-all hover:opacity-90" style={{ background: '#C9A84C', color: '#3A2800' }} data-testid="quiz-result-cta-percorsi">Migliora il tuo punteggio →</button>
              <button onClick={() => navigate('/calcolatori')} className="font-medium text-sm px-5 py-2.5 rounded-lg transition-all hover:bg-white/[0.06]" style={{ border: '1px solid rgba(255,255,255,0.15)', color: '#E8E8E4' }} data-testid="quiz-result-cta-calc">Esplora i calcolatori gratuiti →</button>
            </div>
            {/* Share button */}
            <button
              onClick={async () => {
                const text = `Ho ottenuto ${score}/21 di salute finanziaria su SAPIENS 💰\nQual è il tuo punteggio?\n→ finsapiens.it/quiz`;
                if (navigator.share) {
                  try { await navigator.share({ title: 'Il mio punteggio SAPIENS', text }); } catch {}
                } else {
                  try { await navigator.clipboard.writeText(text); } catch {}
                }
              }}
              className="text-xs font-medium px-4 py-2 rounded-lg transition-all mb-3 block mx-auto"
              style={{ border: '1px solid rgba(201,168,76,0.4)', color: '#C9A84C', background: 'rgba(201,168,76,0.06)' }}
              data-testid="quiz-share-btn"
            >
              Condividi il tuo punteggio
            </button>
            <button onClick={reset} className="text-xs text-[#888780] hover:text-[#E8E8E4] transition-colors">Rifai il quiz</button>
          </motion.div>
        ) : (
          <div>
            {/* Progress bar */}
            <div className="flex items-center gap-3 mb-6">
              <div className="flex-1 h-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.08)' }}>
                <div className="h-full rounded-full transition-all duration-300" style={{ width: `${(step / QUESTIONS.length) * 100}%`, background: '#C9A84C' }} />
              </div>
              <span className="text-xs text-[#888780] shrink-0">{step + 1}/7</span>
            </div>
            <AnimatePresence mode="wait">
              <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
                <p className="text-base font-medium text-[#E8E8E4] mb-4" data-testid={`quiz-question-${step}`}>{QUESTIONS[step].q}</p>
                <div className="space-y-2">
                  {QUESTIONS[step].opts.map((opt, i) => (
                    <button key={i} onClick={() => handleAnswer(i)} className="w-full text-left px-4 py-3 rounded-xl text-sm text-[#888780] hover:text-[#E8E8E4] transition-all" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
                      data-testid={`quiz-opt-${step}-${i}`}>
                      {opt}
                    </button>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        )}
      </div>
    </section>
  );
};

const PensionSimulator = () => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const [eta, setEta] = useState(35);
  const [stipendio, setStipendio] = useState(2000);
  const [annoInizio, setAnnoInizio] = useState(currentYear - 8);
  // Deployment 28 — Fix bug J1/E: separazione tra input grezzo (string) e valore
  // numerico (clampato). onChange accetta qualsiasi input intermedio (anche vuoto),
  // il clamp Math.min/max scatta SOLO su onBlur. Consente edit libero senza scatti.
  const [etaRaw, setEtaRaw] = useState('35');
  const [stipendioRaw, setStipendioRaw] = useState('2000');
  const [annoInizioRaw, setAnnoInizioRaw] = useState(String(currentYear - 8));

  const yearsWorked = Math.max(0, currentYear - annoInizio);
  const pensionRate = Math.min(yearsWorked / 40, 1) * 0.55;
  const pensionMonthly = Math.round(stipendio * pensionRate);
  const gap = Math.max(0, stipendio - pensionMonthly);
  const yearsLeft = Math.max(0, 67 - eta);
  const retirementYear = currentYear + yearsLeft;
  const monthlyNeeded = Math.round(gap * 0.25);
  const barPct = stipendio > 0 ? Math.round((pensionMonthly / stipendio) * 100) : 0;
  const fmtN = (n) => new Intl.NumberFormat('it-IT').format(n);

  return (
    <div className="max-w-4xl mx-auto">
      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        {[
          { label: 'Età attuale', raw: etaRaw, setRaw: setEtaRaw, commit: setEta, min: 18, max: 75, suffix: 'anni', tid: 'pension-input-eta' },
          { label: 'Stipendio netto mensile', raw: stipendioRaw, setRaw: setStipendioRaw, commit: setStipendio, min: 500, max: 20000, suffix: '€', tid: 'pension-input-stipendio' },
          { label: 'Anno inizio lavoro', raw: annoInizioRaw, setRaw: setAnnoInizioRaw, commit: setAnnoInizio, min: 1970, max: currentYear, suffix: '', tid: 'pension-input-anno' },
        ].map(({ label, raw, setRaw, commit, min, max, suffix, tid }) => (
          <div key={tid} className="sapiens-card p-4">
            <label className="text-xs text-[#888780] mb-2 block">{label}</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={raw}
                min={min}
                max={max}
                onChange={(e) => setRaw(e.target.value)}
                onBlur={(e) => {
                  const n = Number(e.target.value);
                  const clamped = Number.isFinite(n) && e.target.value !== ''
                    ? Math.min(max, Math.max(min, n))
                    : min;
                  setRaw(String(clamped));
                  commit(clamped);
                }}
                className="flex-1 bg-transparent text-xl font-semibold text-[#E8E8E4] outline-none w-0 min-w-0"
                data-testid={tid}
              />
              {suffix && <span className="text-[#888780] text-sm shrink-0">{suffix}</span>}
            </div>
          </div>
        ))}
      </div>
      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        <div className="sapiens-card p-6 text-center" style={{ background: '#1A2040', borderColor: 'rgba(61,90,180,0.3)' }} data-testid="pension-result-card">
          <p className="text-xs text-[#888780] mb-2">Pensione INPS stimata</p>
          <p className="text-2xl font-bold" style={{ color: '#C9A84C' }}>€{fmtN(pensionMonthly)}</p>
          <p className="text-xs text-[#888780] mt-1">al mese</p>
        </div>
        <div className="sapiens-card p-6 text-center" style={{ background: '#1F1A1A', borderColor: 'rgba(240,149,149,0.3)' }}>
          <p className="text-xs text-[#888780] mb-2">Gap mensile</p>
          <p className="text-2xl font-bold text-[#F09595]">-€{fmtN(gap)}</p>
          <p className="text-xs text-[#888780] mt-1">differenza da colmare</p>
        </div>
        <div className="sapiens-card p-6 text-center" style={{ background: '#1F1C14', borderColor: 'rgba(201,168,76,0.3)' }}>
          <p className="text-xs text-[#888780] mb-2">Anni alla pensione</p>
          <p className="text-2xl font-bold text-[#C9A84C]">{yearsLeft}</p>
          <p className="text-xs text-[#888780] mt-1">pensione nel {retirementYear}</p>
        </div>
      </div>
      <div className="sapiens-card p-6 mb-6">
        <p className="text-sm font-medium text-[#E8E8E4] mb-4">Confronto visivo</p>
        <div className="space-y-3">
          <div>
            <div className="flex justify-between text-xs text-[#888780] mb-1"><span>Stipendio attuale</span><span>€{fmtN(stipendio)}/mese</span></div>
            <div className="h-3 rounded-full bg-white/[0.06]"><div className="h-full rounded-full bg-[#3D7A52]" style={{ width: '100%' }} /></div>
          </div>
          <div>
            <div className="flex justify-between text-xs text-[#888780] mb-1"><span>Pensione stimata ({barPct}%)</span><span>€{fmtN(pensionMonthly)}/mese</span></div>
            <div className="h-3 rounded-full bg-white/[0.06]"><div className="h-full rounded-full transition-all" style={{ width: `${barPct}%`, background: barPct < 60 ? '#F09595' : '#C9A84C' }} /></div>
          </div>
        </div>
      </div>
      {gap > 0 && (
        <div className="sapiens-card p-6 text-center" style={{ border: '1px solid rgba(201,168,76,0.4)', background: 'rgba(201,168,76,0.04)' }}>
          <p className="text-[#E8E8E4] text-base mb-2">
            Per colmare il gap di <strong style={{ color: '#C9A84C' }}>€{fmtN(gap)}/mese</strong> dovresti investire circa{' '}
            <strong style={{ color: '#C9A84C' }}>€{fmtN(monthlyNeeded)}/mese</strong> oggi.
          </p>
          <p className="text-sm text-[#888780] mb-4">Prima inizi, minore sarà lo sforzo mensile grazie all'interesse composto.</p>
          <button onClick={() => navigate('/calcolatori?tab=Previdenza')} className="sapiens-btn-primary px-6 py-2.5 text-sm" data-testid="pension-sim-cta-btn">
            Pianifica la tua pensione →
          </button>
        </div>
      )}
    </div>
  );
};

const HomePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [lessons, setLessons] = useState([]);
  const [lessonCount, setLessonCount] = useState(null);
  const [publicStats, setPublicStats] = useState(null);
  const [counters, setCounters] = useState(null);
  const [articoloGiorno, setArticoloGiorno] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/lessons`).then(r => r.json()).then(data => {
      setLessons(data);
      setLessonCount(data.length);
    }).catch(console.error);
    fetch(`${API_URL}/api/stats/public`).then(r => r.json()).then(setPublicStats).catch(() => {});
    // Deployment 16 — P0b: badge homepage dinamici da /api/counters (DB counts, cached 60s)
    fetch(`${API_URL}/api/counters`).then(r => r.json()).then(setCounters).catch(() => {});
    // Widget articolo del giorno: ottieni articolo in base al giorno della settimana
    fetch(`${API_URL}/api/blog/posts?limit=14`).then(r => r.json()).then(posts => {
      if (posts && posts.posts && posts.posts.length > 0) {
        const dayIdx = new Date().getDay();
        const idx = dayIdx % posts.posts.length;
        setArticoloGiorno(posts.posts[idx]);
      }
    }).catch(() => {});
  }, []);

  const features = [
    { icon: '🎓', title: 'Impara', description: 'Percorsi strutturati dal risparmio agli investimenti avanzati' },
    { icon: '⚡', title: 'Pratica', description: 'Quiz interattivi per consolidare le tue conoscenze' },
    { icon: '🧮', title: 'Simula', description: 'Calcolatori per pianificare il tuo futuro finanziario' }
  ];

  const levels = [
    { level: 1, name: 'Base', description: 'Concetti di denaro, risparmio, conto corrente', icon: '🌱', lessons: lessons.filter(l => l.level === 1 && !l.is_level_quiz).length || 5, time: 18 },
    { level: 2, name: 'Intermedio', description: 'Investimenti, ETF, obbligazioni', icon: '📈', lessons: lessons.filter(l => l.level === 2 && !l.is_level_quiz).length || 5, time: 27 },
    { level: 3, name: 'Avanzato', description: 'Portafoglio, asset allocation, rischio', icon: '💼', lessons: lessons.filter(l => l.level === 3 && !l.is_level_quiz).length || 3, time: 23, premium: true },
    { level: 4, name: 'Esperto', description: 'Fiscalità, pianificazione, previdenza', icon: '🎓', lessons: lessons.filter(l => l.level === 4 && !l.is_level_quiz).length || 3, time: 29, premium: true }
  ];

  return (
    <div className="min-h-screen bg-[#1C1C1A]">
      <Helmet>
        <title>SAPIENS — Educazione Finanziaria Gratuita in Italiano</title>
        <meta name="description" content="Impara la finanza personale con 34 calcolatori, 23 lezioni pratiche e quiz interattivi. Gratis, in italiano, senza conflitti di interesse." />
        <link rel="canonical" href="https://finsapiens.it/" />
      </Helmet>
      {/* ── HERO ── */}
      <section className="relative min-h-[90vh] flex flex-col justify-center pt-20 pb-16 lg:pb-28" data-testid="hero-section">
        <div className="container-wide">
          <div className="max-w-3xl">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55 }}>

              {/* Brand logo */}
              <div className="flex items-center gap-2" style={{ marginBottom: '20px' }}>
                <span className="rounded-full bg-[#C9A84C] shrink-0" style={{ width: '10px', height: '10px' }} />
                <span className="text-[#E8E8E4] font-semibold" style={{ fontSize: '22px', letterSpacing: '0.12em' }} data-testid="hero-brand">SAPIENS</span>
              </div>

              {/* Tagline */}
              <p className="tagline" style={{ fontSize: '16px', opacity: 0.75, marginBottom: '24px' }}>
                Make sense of your money.
              </p>

              {/* Social proof row */}
              <motion.div
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
                className="flex flex-wrap items-center gap-3 mb-8"
                data-testid="social-proof-row"
              >
                <div className="flex">
                  {[0,1,2,3,4].map(i => <Star key={i} className="w-3.5 h-3.5 fill-[#C9A84C] text-[#C9A84C]" />)}
                </div>
                <span className="text-sm text-[#888780]">Ora in beta gratuita</span>
                <span className="text-xs px-3 py-1 rounded-full" style={{
                  background: 'rgba(61,122,82,0.2)',
                  color: '#9FE1CB',
                  border: '1px solid rgba(61,122,82,0.4)'
                }} data-testid="beta-pill">Accesso anticipato</span>
              </motion.div>

              {/* H1 */}
              <motion.h1
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
                className="font-bold mb-6"
                style={{ fontSize: 'clamp(1.8rem, 4vw, 2.4rem)', lineHeight: 1.25 }}
                data-testid="hero-headline"
              >
                <span className="block text-[#E8E8E4]">Gestire i soldi non si impara a scuola.</span>
                <span className="block text-[#C9A84C]">Per questo ci siamo noi.</span>
              </motion.h1>

              {/* Subheadline */}
              <motion.p
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }}
                className="mb-8 max-w-2xl"
                style={{ color: '#888780', fontSize: '16px', lineHeight: 1.7 }}
                data-testid="hero-subheadline"
              >
                Dal conto corrente agli ETF, dal mutuo alla pensione complementare. Lezioni chiare, quiz pratici e calcolatori reali, senza gergo tecnico.
              </motion.p>

              {/* Stats badges */}
              <motion.div
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }}
                className="flex flex-wrap gap-2 mb-10"
                data-testid="stats-badges"
              >
                <StatBadge label="Gratuito" gold />
                {/* Deployment 15 — P0 Fix 4: Badge "X utenti" nascosto in homepage */}
                {/* <StatBadge label={`${publicStats ? publicStats.users : '—'} utenti`} /> */}
                <StatBadge label={`${counters?.calculators ?? '—'} calcolatori`} data-testid="badge-calculators" />
                <StatBadge label={`${counters?.lessons ?? '—'} lezioni`} data-testid="badge-lessons" />
                <StatBadge label="4 livelli" data-testid="badge-livelli" />
                <StatBadge label={`${counters?.glossary ?? '—'} termini`} data-testid="badge-glossary" />
              </motion.div>

              {/* CTA Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.65 }}
                className="flex flex-wrap gap-4"
              >
                <button
                  onClick={() => { const el = document.getElementById('quiz-section'); el && el.scrollIntoView({ behavior: 'smooth' }); }}
                  className="font-semibold transition-all duration-200 hover:opacity-90 hover:-translate-y-0.5 flex items-center gap-2"
                  style={{ background: '#C9A84C', color: '#3A2800', padding: '13px 28px', borderRadius: '8px', fontSize: '15px' }}
                  data-testid="hero-cta"
                >
                  Scopri il tuo punteggio finanziario <ChevronRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => navigate('/calcolatori/stipendio-netto')}
                  className="font-medium transition-all duration-200 hover:bg-white/5"
                  style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.25)', color: '#E8E8E4', padding: '13px 28px', borderRadius: '8px', fontSize: '15px' }}
                  data-testid="hero-secondary-cta"
                >
                  Calcola il tuo stipendio netto →
                </button>
              </motion.div>

            </motion.div>
          </div>
        </div>
      </section>

      {/* ── QUIZ AUTOVALUTAZIONE ── */}
      <QuizSection navigate={navigate} />

      {/* ── SIMULATORE PENSIONE (dopo quiz, prima calcolatori) ── */}
      <section className="py-20 bg-[#181816]" data-testid="pension-simulator-section">
        <div className="container-wide">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-medium text-[#E8E8E4] mb-4">Scopri il tuo gap pensionistico</h2>
            <p className="text-lg text-[#888780] max-w-2xl mx-auto">Inserisci i tuoi dati e vedi subito quanto prenderai di pensione pubblica. Aggiornamento in tempo reale.</p>
          </div>
          <PensionSimulator />
        </div>
      </section>

      {/* ── COSA SCOPRI SUBITO ── */}
      <section className="py-10" style={{ background: '#1C1C1A' }} data-testid="cosa-scopri-section">
        <div className="container-wide">
          <div className="text-center mb-6">
            <h2 className="font-bold text-[#E8E8E4] mb-1" style={{ fontSize: 22 }}>Cosa puoi scoprire adesso, gratis</h2>
            <p className="text-xs text-[#888780]">Senza registrarti. Risultati immediati.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { icon: '💼', title: 'Il tuo stipendio netto reale', desc: 'Inserisci la RAL e scopri quanto ricevi davvero in busta paga ogni mese.', cta: 'Calcola ora →', href: '/calcolatori/stipendio-netto' },
              { icon: '🏠', title: 'Quanto ti costa davvero il mutuo', desc: 'Rata mensile, interessi totali e tutti i costi nascosti che raramente ti vengono comunicati chiaramente. Inserisci i tuoi dati e scoprilo.', cta: 'Calcola ora →', href: '/calcolatori/mutuo' },
              { icon: '🔥', title: 'Quando potresti smettere di lavorare', desc: 'Calcola la tua data di libertà finanziaria con il Simulatore FIRE — basato sui tuoi numeri reali.', cta: 'Calcola ora →', href: '/calcolatori/simulatore-fire' },
            ].map((c, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="p-6 rounded-xl cursor-pointer transition-all hover:border-[#C9A84C]/30" style={{ background: '#252523', border: '1px solid rgba(255,255,255,0.06)' }}
                onClick={() => navigate(c.href)}>
                <div className="text-2xl mb-3">{c.icon}</div>
                <h3 className="font-semibold text-[#E8E8E4] mb-2" style={{ fontSize: 14 }}>{c.title}</h3>
                <p className="text-xs text-[#888780] mb-4 leading-relaxed">{c.desc}</p>
                <button className="text-xs font-medium transition-colors hover:opacity-80" style={{ color: '#C9A84C' }}>{c.cta}</button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WIDGET ARTICOLO DEL GIORNO ── */}
      {articoloGiorno && (
        <section className="py-8" style={{ background: '#181816' }} data-testid="articolo-giorno-section">
          <div className="container-wide">
            <div
              onClick={() => navigate(`/blog/${articoloGiorno.slug}`)}
              className="flex items-start gap-4 p-5 rounded-2xl cursor-pointer transition-all hover:border-[#C9A84C]/40 group"
              style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.2)' }}
              data-testid="articolo-giorno-widget"
            >
              <div className="flex-shrink-0 mt-0.5">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold" style={{ background: 'rgba(201,168,76,0.15)', color: '#C9A84C' }}>📰</div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-semibold text-[#C9A84C] tracking-wide uppercase">Articolo del giorno</span>
                  {articoloGiorno.reading_time && <span className="text-xs text-[#888780]">· {articoloGiorno.reading_time} min</span>}
                </div>
                <h3 className="text-sm font-semibold text-[#E8E8E4] group-hover:text-[#C9A84C] transition-colors leading-snug">{articoloGiorno.title}</h3>
                {articoloGiorno.excerpt && <p className="text-xs text-[#888780] mt-1 line-clamp-1">{articoloGiorno.excerpt}</p>}
              </div>
              <span className="text-[#888780] group-hover:text-[#C9A84C] transition-colors text-sm mt-1 flex-shrink-0">→</span>
            </div>
          </div>
        </section>
      )}

      {/* ── FEATURES ── */}
      <section id="features-section" data-testid="features-section" className="py-20 bg-[#1C1C1A]">        <div className="container-wide">
          <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl sm:text-4xl font-medium text-[#E8E8E4] mb-4">Il tuo percorso verso la libertà finanziaria</h2>
            <p className="text-[#888780] text-lg max-w-2xl mx-auto">Tre pilastri per costruire solide competenze finanziarie</p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6 mb-20">
            {features.map((feature, index) => (
              <motion.div key={feature.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="sapiens-card p-8 card-hover" data-testid={`feature-card-${feature.title.toLowerCase()}`}>
                <div className="text-4xl mb-6">{feature.icon}</div>
                <h3 className="text-xl font-medium text-[#E8E8E4] mb-3">{feature.title}</h3>
                <p className="text-[#888780]">{feature.description}</p>
              </motion.div>
            ))}
          </div>
          <motion.div className="text-center mb-12" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl sm:text-4xl font-medium text-[#E8E8E4] mb-4">4 livelli di apprendimento</h2>
            <p className="text-[#888780] text-lg max-w-2xl mx-auto">Dalla gestione base del denaro alla pianificazione avanzata</p>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {levels.map((level, index) => (
              <motion.div key={level.level} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="sapiens-card p-6 card-hover cursor-pointer relative" onClick={() => navigate('/percorsi')} data-testid={`level-preview-${level.level}`}>
                {level.premium && <div className="absolute top-4 right-4"><span className="premium-badge text-[10px]">Premium</span></div>}
                <div className="text-4xl mb-4">{level.icon}</div>
                <LevelBadge level={level.level} />
                <h3 className="text-lg font-medium text-[#E8E8E4] mt-3 mb-2">{level.name}</h3>
                <p className="text-sm text-[#888780] mb-4">{level.description}</p>
                <div className="text-xs text-[#888780]">{level.lessons} lezioni · {level.time} min</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── BETA TESTIMONIALS PLACEHOLDER ── */}
      <section className="py-20 bg-[#252523]" data-testid="testimonials-section">
        <div className="container-wide">
          <div className="text-center py-12">
            <div className="flex justify-center gap-1 mb-6">
              {[0,1,2,3,4].map(i => <Star key={i} className="w-5 h-5 text-[#C9A84C]/30" />)}
            </div>
            <p className="text-[#888780] text-base italic max-w-md mx-auto leading-relaxed" data-testid="beta-reviews-message">
              Le recensioni dei nostri studenti appariranno dopo il lancio beta.
            </p>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="py-20 bg-[#1C1C1A]">
        <div className="container-wide">
          <motion.div className="sapiens-card p-12 text-center border-[#C9A84C]/20" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl sm:text-4xl font-medium text-[#E8E8E4] mb-4">Pronto a iniziare?</h2>
            <p className="text-lg text-[#888780] mb-8 max-w-2xl mx-auto">Educazione finanziaria gratuita, chiara e pratica. Senza gergo tecnico.</p>
            <button
              onClick={() => navigate(user ? '/dashboard' : '/login')}
              style={{ background: '#C9A84C', color: '#3A2800', padding: '14px 32px', borderRadius: '8px', fontSize: '16px', fontWeight: 600 }}
              className="hover:opacity-90 transition-opacity"
              data-testid="final-cta"
            >
              Entra in beta ›
            </button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
